import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Plus } from 'lucide-react'
import { createColumns, BroadcastDeviceAction } from './columns'
import { DataTable } from './data-table'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { useBroadcastDevicesByClinicId } from '@/hooks/useBroadcastDevicesByClinicId'
import { useViewMode } from '@/stores/viewMode'
import { BroadcastDeviceCreateDialog } from './BroadcastDeviceCreateDialog'
import { BroadcastDeviceSettingDialog } from './BroadcastDeviceSettingDialog'
import { DeleteAlert } from '@/components/DeleteAlert'
import { useAuth } from '@/hooks/useAuth'
import { broadcastDeviceService } from '@/services/broadcast_device_service'
import { toast } from 'sonner'
import IfAnyPermission from '@/components/IfAnyPermission'
import { AllowedAction } from '@/data/allowedActions'

export default function BroadcastDevicesPage() {
  const { id: clinicId } = useViewMode()
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingDeviceId, setEditingDeviceId] = useState<number | null>(null)
  const [deletingDeviceId, setDeletingDeviceId] = useState<number | null>(null)
  const { token } = useAuth()

  const { devices, pagination, isLoading, isError, mutate } = useBroadcastDevicesByClinicId({
    page,
    pageSize,
    clinic_id: clinicId,
  })

  // 處理設備操作（編輯或刪除）
  const handleDeviceAction = (action: BroadcastDeviceAction) => {
    if (action.type === 'edit') {
      setEditingDeviceId(action.deviceId)
    } else if (action.type === 'delete') {
      setDeletingDeviceId(action.deviceId)
    }
  }

  // 處理刪除設備
  const handleDeleteDevice = async () => {
    if (!deletingDeviceId || !token) return

    try {
      const response = await broadcastDeviceService.deleteBroadcastDevice(deletingDeviceId, token)

      if (!response.success) {
        throw new Error(response.error?.message || '刪除廣播設備失敗')
      }

      toast.success('刪除成功')

      // 重新加載數據
      mutate()
      // 關閉刪除對話框
      setDeletingDeviceId(null)
    } catch {
      toast.error('刪除失敗')
    }
  }

  if (isError) {
    return (
      <div>
        <div className="text-center text-destructive">載入裝置列表失敗。請稍後再試。</div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-8 w-[200px] mb-4" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  // Generate page numbers for pagination
  const generatePaginationItems = () => {
    if (!pagination) return []

    const items = []
    const { page: currentPage, totalPages } = pagination

    // Always show first page
    items.push(1)

    if (currentPage > 3) {
      items.push('ellipsis')
    }

    // Show pages around current page
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      items.push(i)
    }

    if (currentPage < totalPages - 2) {
      items.push('ellipsis')
    }

    // Always show last page
    if (totalPages > 1) {
      items.push(totalPages)
    }

    return items
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">裝置管理</h2>
          <p className="text-muted-foreground mt-2">診所內的裝置管理</p>
        </div>
        {/*
          裝置 CRUD 後端限定 admin + provider only（POST /api/clinic/:clinic_id/broadcast-device
          走 checkAnyPermission + checkAdminOrProviderClinicAccess）。FE 按鈕鏡像同樣的 gate，
          clinic / clinic_group user 即使有相關權限也不顯示，避免按下去 403。
        */}
        <IfAnyPermission
          permissions={[
            { module: 'system_clinic', action: AllowedAction.C },
            { module: 'system_provider', action: AllowedAction.C },
            { module: 'provider_clinic_management', action: AllowedAction.C },
          ]}
        >
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            新增裝置
          </Button>
        </IfAnyPermission>
      </div>

      <Card>
        <CardContent className="p-6">
          <DataTable
            columns={createColumns({ onAction: handleDeviceAction })}
            data={devices || []}
            meta={{
              mutate: mutate as () => Promise<void>,
            }}
          />

          {pagination && pagination.totalPages > 1 && (
            <div className="mt-4 flex justify-center">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        if (page > 1) setPage(page - 1)
                      }}
                    />
                  </PaginationItem>

                  {generatePaginationItems().map((item, index) => {
                    if (item === 'ellipsis') {
                      return (
                        <PaginationItem key={`ellipsis-${index}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      )
                    }

                    return (
                      <PaginationItem key={item}>
                        <PaginationLink
                          href="#"
                          isActive={item === page}
                          onClick={(e) => {
                            e.preventDefault()
                            setPage(item as number)
                          }}
                        >
                          {item}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  })}

                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        if (pagination && page < pagination.totalPages) setPage(page + 1)
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>

      <BroadcastDeviceCreateDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        clinicId={clinicId}
        onSuccess={mutate}
      />

      {/* 編輯設備對話框 - 使用固定的設備ID而不是依賴於行索引 */}
      {editingDeviceId && (
        <BroadcastDeviceSettingDialog
          broadcastDeviceId={editingDeviceId}
          clinicId={clinicId}
          open={editingDeviceId !== null}
          onOpenChange={(open) => {
            if (!open) setEditingDeviceId(null)
          }}
          onMutate={mutate}
        />
      )}

      {/* 刪除設備對話框 */}
      <DeleteAlert
        open={deletingDeviceId !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingDeviceId(null)
        }}
        onConfirm={handleDeleteDevice}
        title="確認刪除廣播設備"
        description="您確定要刪除此廣播設備嗎？此操作無法復原。"
      />
    </div>
  )
}
